import React, { ReactNode, useEffect } from 'react';
import styles from './index.module.scss';

export interface ModalProps {
  /** Controls visibility. When false, nothing is rendered. */
  open: boolean;
  /** Invoked when the user requests to close the modal (backdrop click or Esc). */
  onClose: () => void;
  children: ReactNode;
  /** Accessible label for the dialog. */
  ariaLabel?: string;
  'data-testid'?: string;
}

/**
 * Simple in-app modal: a fixed full-screen backdrop with a centered, dark
 * panel matching the portal theme. Closes on backdrop click + Escape, and
 * locks body scroll while open.
 */
function Modal({
  open,
  onClose,
  children,
  ariaLabel,
  'data-testid': dataTestId,
}: ModalProps) {
  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className={styles.backdrop}>
      {/* Full-cover button (behind the panel) closes on click; native button is
          keyboard-accessible, so no a11y handlers are needed on the divs. Esc is
          handled above. Clicks on the panel land on the panel (stacked above) and
          do not reach this button, so they don't close the modal. */}
      <button
        type="button"
        className={styles.backdropButton}
        aria-label={ariaLabel ? `Close ${ariaLabel}` : 'Close'}
        data-testid={dataTestId}
        onClick={onClose}
      />
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>
  );
}

export default Modal;
